const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_RETRY_DELAY_MS = 1000;

export type ManagerAuthBootstrapResult = { accessToken: string; user: unknown };

export type ManagerAuthBootstrapOutcome =
  | { type: 'authenticated'; user: unknown }
  | { type: 'mismatch' }
  | { type: 'refresh-failed'; error: unknown };

export type ManagerAuthBootstrapDeps = {
  consumeSkipAutoLogin: () => boolean;
  fetchManagerAccessToken: () => Promise<ManagerAuthBootstrapResult>;
  maxRetries?: number;
  retryDelayMs?: number;
};

/**
 * Runs the manager iframe's own "am I still logged in" bootstrap: a `consumeSkipAutoLogin`
 * short-circuit, otherwise fetching a fresh token with a couple of silent retries on a plain
 * failure (network blip, cookie not yet written) before reporting a definite outcome.
 *
 * `fetchManagerAccessToken` resolving with an empty `accessToken` means a cross-tab identity
 * mismatch was detected (reported as 'mismatch'); it *rejecting* means some other, unrelated
 * failure (reported as 'refresh-failed', only after retries are exhausted) - these must stay
 * distinguishable, since only a genuine mismatch justifies ending the outer recruiter session.
 *
 * Returns a cancel function; calling it suppresses `onOutcome` for any attempt still in flight
 * (e.g. the component unmounted) and stops a pending retry from firing.
 */
export function runManagerAuthBootstrap(
  {
    consumeSkipAutoLogin,
    fetchManagerAccessToken,
    maxRetries = DEFAULT_MAX_RETRIES,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  }: ManagerAuthBootstrapDeps,
  onOutcome: (outcome: ManagerAuthBootstrapOutcome) => void,
): () => void {
  let cancelled = false;
  let retryTimeout: ReturnType<typeof setTimeout> | undefined;

  const finish = (outcome: ManagerAuthBootstrapOutcome) => {
    if (!cancelled) onOutcome(outcome);
  };

  const attempt = (retriesLeft: number) => {
    fetchManagerAccessToken()
      .then((result) => {
        if (cancelled) return;
        finish(result.accessToken ? { type: 'authenticated', user: result.user } : { type: 'mismatch' });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (retriesLeft > 0) {
          retryTimeout = setTimeout(() => attempt(retriesLeft - 1), retryDelayMs);
          return;
        }
        finish({ type: 'refresh-failed', error });
      });
  };

  if (consumeSkipAutoLogin()) {
    Promise.resolve().then(() => finish({ type: 'mismatch' }));
  } else {
    attempt(maxRetries);
  }

  return () => {
    cancelled = true;
    clearTimeout(retryTimeout);
  };
}
